import { useEffect, useMemo, useRef, useState } from 'react';
import { useOrgFilter, pickDefaultOrg } from '@/store/defaults';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { orgs } from '@/api/catalogs';
import {
  staffSchedules,
  positionsApi,
  departmentsApi,
  calcTypesApi,
  type StaffScheduleItem,
  type StaffScheduleListRow,
} from '@/api/hr';
import { usePermissionsByOrg } from '@/hooks/usePermissionsByOrg';
import type { ColDef } from 'ag-grid-community';
import ImportedBadge from '@/shared/components/ImportedBadge';
import InfiniteDocGrid, { TitleTotal } from '@/shared/components/InfiniteDocGrid';
import { useInfiniteGridDatasource } from '@/hooks/useInfiniteGridDatasource';
import { useSignControlInfinite } from '@/shared/hooks/useSignControl';
import DatePopover from '@/components/DatePopover';
import AttachmentsPanel from '@/shared/components/AttachmentsPanel';

/**
 * Штатний розклад (doc.staff_schedules). Два режими:
 * - «Документи» — список документів штатного розкладу + редактор (шапка + позиції:
 *   підрозділ / посада / кількість ставок / оклад). Проведення фіксує стан.
 * - «Зріз на дату» — штатний розклад станом на обрану дату (будується з проведених
 *   документів на backend). Для друку (окрема сторінка).
 *
 * Permission: documents.staff_schedules.create (CRUD) / .post (проведення).
 */
const PERM = 'documents.staff_schedules.create';

type Tab = 'docs' | 'snapshot';

// Вид розрахунку (calc_type_code зі стану) → людська назва для зрізу.
const CALC_TYPE_LABELS: Record<string, string> = {
  SALARY: 'Оклад (місячна ставка)',
  HOURLY: 'Погодинна оплата',
  PIECEWORK: 'Відрядна оплата',
  AVERAGE: 'За середнім заробітком',
};

interface ItemDraft {
  line_number: number;
  department_id: string;
  position_id: string;
  rate_count: string;
  salary: string;
  calc_type_id: string;
}

export default function StaffSchedulesPage() {
  const qc = useQueryClient();
  const orgList = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  const perms = usePermissionsByOrg(PERM);
  const allowedOrgIds = useMemo(() => new Set(perms.availableOrgs.map((o) => o.id)), [perms.availableOrgs]);

  // Орг за замовчуванням з шапки (override при ручному виборі).
  const [orgId, setOrgId] = useOrgFilter();
  useEffect(() => {
    const first = pickDefaultOrg(orgList.data?.items);
    if (!orgId && first) setOrgId(first.id);
  }, [orgList.data, orgId]);

  const [tab, setTab] = useState<Tab>('docs');
  const canManage = !!orgId && allowedOrgIds.has(orgId);

  // Довідники для резолву назв + dropdown.
  const deptsQ = useQuery({
    queryKey: ['departments', orgId],
    queryFn: () => departmentsApi.list({ organization_id: orgId, limit: 1000 }),
    enabled: !!orgId,
  });
  const posQ = useQuery({
    queryKey: ['positions-all'],
    queryFn: () => positionsApi.list({ limit: 1000 }),
  });
  // Види розрахунку: лише оклад/погодинна (як у наказах, рішення власника).
  const calcQ = useQuery({ queryKey: ['calc-types'], queryFn: calcTypesApi.list });
  const calcTypes = useMemo(
    () => (calcQ.data ?? []).filter((c) => c.code === 'SALARY' || c.code === 'HOURLY'),
    [calcQ.data]);
  const deptName = useMemo(() => {
    const m = new Map<string, string>();
    deptsQ.data?.items.forEach((d) => m.set(d.id, d.name));
    return m;
  }, [deptsQ.data]);
  const posName = useMemo(() => {
    const m = new Map<string, string>();
    posQ.data?.items.forEach((p) => m.set(p.id, p.name));
    return m;
  }, [posQ.data]);

  return (
    <div className="card">
      <div className="toolbar" style={{ gap: '0.4rem', flexWrap: 'wrap' }}>
        <h2 style={{ margin: 0 }}>Штатний розклад</h2>
        <select value={orgId} onChange={(e) => setOrgId(e.target.value)} style={{ width: 220, flex: '0 0 auto' }}>
          {orgList.data?.items.map((o) => (
            <option key={o.id} value={o.id}>{o.name} — {o.code}</option>
          ))}
        </select>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 4 }}>
          <button className={tab === 'docs' ? 'primary' : ''} onClick={() => setTab('docs')}>Документи</button>
          <button className={tab === 'snapshot' ? 'primary' : ''} onClick={() => setTab('snapshot')}>Зріз на дату</button>
        </div>
      </div>

      {tab === 'docs' ? (
        <DocsTab
          orgId={orgId} canManage={canManage} qc={qc}
          depts={deptsQ.data?.items ?? []} positions={posQ.data?.items ?? []}
          calcTypes={calcTypes}
        />
      ) : (
        <SnapshotTab orgId={orgId} deptName={deptName} posName={posName} />
      )}
    </div>
  );
}

// ============================================================ Документи

interface DocsTabProps {
  orgId: string; canManage: boolean; qc: ReturnType<typeof useQueryClient>;
  depts: { id: string; name: string }[];
  positions: { id: string; name: string }[];
  calcTypes: { id: string; code: string; name: string }[];
}

function DocsTab({ orgId, canManage, qc, depts, positions, calcTypes }: DocsTabProps) {
  // 2026-08-23 (Норма #29): серверна пагінація + сортування; «Підпис» —
  // sign-control doc.staff_schedules.
  const refreshRef = useRef<(() => void) | null>(null);
  const { datasource, total } = useInfiniteGridDatasource<StaffScheduleListRow>(
    async (limit, offset, sort) => {
      if (!orgId) return { items: [], total: 0 };
      return staffSchedules.page({
        organization_id: orgId, sort_by: sort?.sort_by, sort_dir: sort?.sort_dir, limit, offset,
      });
    },
    [orgId],
    { attachmentsType: 'doc.staff_schedules' },
  );
  const sc = useSignControlInfinite<StaffScheduleListRow>('doc.staff_schedules', orgId);
  const invalidate = () => {
    refreshRef.current?.();
    qc.invalidateQueries({ queryKey: ['staff-schedules', orgId] });
    qc.invalidateQueries({ queryKey: ['staff-schedule-snapshot'] });
  };

  // ── Редактор (drawer) ──
  const [editId, setEditId] = useState<string | null>(null); // null=закрито, ''=новий
  const [docDate, setDocDate] = useState('');
  const [kind, setKind] = useState<'INITIAL' | 'CHANGE'>('INITIAL');
  const [comment, setComment] = useState('');
  const [items, setItems] = useState<ItemDraft[]>([]);
  const [posted, setPosted] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const openNew = () => {
    setEditId(''); setDocDate(new Date().toISOString().slice(0, 10));
    setKind('INITIAL'); setComment(''); setItems([]); setPosted(false); setErr(null);
  };
  const openEdit = async (id: string) => {
    const d = await staffSchedules.get(id);
    setEditId(id);
    setDocDate(d.document_date.slice(0, 10));
    setKind(d.kind); setComment(d.comment ?? ''); setPosted(d.posted);
    setItems(d.items.map((it) => ({
      line_number: it.line_number, department_id: it.department_id ?? '',
      position_id: it.position_id, rate_count: it.rate_count, salary: it.salary,
      calc_type_id: it.calc_type_id ?? '',
    })));
    setErr(null);
  };
  const close = () => setEditId(null);

  const addRow = () => setItems([...items, {
    line_number: items.length + 1, department_id: '', position_id: '',
    rate_count: '1', salary: '0', calc_type_id: '',
  }]);
  const setRow = (idx: number, patch: Partial<ItemDraft>) =>
    setItems(items.map((it, i) => (i === idx ? { ...it, ...patch } : it)));
  const delRow = (idx: number) =>
    setItems(items.filter((_, i) => i !== idx).map((it, i) => ({ ...it, line_number: i + 1 })));

  const save = useMutation({
    mutationFn: async (): Promise<string> => {
      const payload = {
        organization_id: orgId,
        document_date: new Date(docDate).toISOString(),
        kind, comment: comment.trim() || null,
        items: items.map((it) => ({
          line_number: it.line_number,
          department_id: it.department_id || null,  // порожньо → без підрозділу (0305)
          position_id: it.position_id, rate_count: it.rate_count, salary: it.salary,
          calc_type_id: it.calc_type_id || null,    // вид розрахунку (0306)
          comment: null,
        })) as StaffScheduleItem[],
      };
      if (editId) {
        const { organization_id: _o, ...rest } = payload;
        const d = await staffSchedules.update(editId, rest);
        return d.id;
      }
      const d = await staffSchedules.create(payload);
      return d.id;
    },
    onSuccess: (id) => { invalidate(); openEdit(id); },
    onError: (e: unknown) => setErr(extractErr(e)),
  });
  const doPost = useMutation({
    mutationFn: () => staffSchedules.post(editId as string),
    onSuccess: (d) => { invalidate(); setPosted(d.posted); },
    onError: (e: unknown) => setErr(extractErr(e)),
  });
  const doUnpost = useMutation({
    mutationFn: () => staffSchedules.unpost(editId as string),
    onSuccess: (d) => { invalidate(); setPosted(d.posted); },
    onError: (e: unknown) => setErr(extractErr(e)),
  });
  const doDelete = useMutation({
    mutationFn: () => staffSchedules.remove(editId as string),
    onSuccess: () => { invalidate(); close(); },
    onError: (e: unknown) => setErr(extractErr(e)),
  });

  // Підрозділ необов'язковий (alembic 0305) — досить посади.
  const validItems = items.length > 0 && items.every((it) => it.position_id);

  const cols = useMemo<ColDef<StaffScheduleListRow>[]>(() => [
    {
      headerName: '', width: 44, sortable: false,
      cellRenderer: (p: { data?: StaffScheduleListRow }) =>
        <ImportedBadge source={p.data?.external_source} />,
    },
    { field: 'document_number', headerName: '№', width: 130 },
    {
      field: 'document_date', headerName: 'Дата', width: 120,
      valueFormatter: (p) => p.value ? String(p.value).slice(0, 10) : '',
    },
    {
      field: 'kind', headerName: 'Вид', width: 130,
      valueFormatter: (p) => p.data ? (p.value === 'CHANGE' ? 'Зміна' : 'Первинний') : '',
    },
    { field: 'items_count', headerName: 'Позицій', width: 100, cellStyle: { textAlign: 'center' } },
    {
      field: 'posted', headerName: 'Проведено', width: 110,
      cellStyle: { textAlign: 'center' },
      cellRenderer: (p: { data?: { posted?: boolean } }) =>
        p.data?.posted ? <span style={{ color: 'var(--badge-green-fg)', fontWeight: 600 }}>✓</span> : '',
    },
    {
      colId: '_att', headerName: 'Вкладення', width: 110, sortable: false,
      cellStyle: { textAlign: 'center' },
      cellRenderer: (p: { data?: { _att?: boolean } }) => p.data?._att ? '✓' : '',
    },
  ], []);

  return (
    <>
      <div style={{ margin: '0.4rem 0' }}>
        <button className="primary" disabled={!canManage} onClick={openNew}>+ Новий документ</button>
      </div>
      <div style={{ fontSize: 13, color: 'var(--muted)', margin: '0 0 0.3rem' }}><TitleTotal total={total} /> документів</div>
      <InfiniteDocGrid<StaffScheduleListRow>
        datasource={sc.wrap(datasource)}
        columnDefs={[...cols, ...sc.column]}
        storageKey="staff-schedules-docs"
        onRowDoubleClicked={(r) => openEdit(r.id)}
        getRowStyle={(p) => p.data?.deletion_mark ? { opacity: 0.4 } : sc.getRowStyle(p)}
        refreshRef={refreshRef}
      />

      {editId !== null && (
        <div style={{
          position: 'fixed', top: 0, right: 0, bottom: 0, width: 640, zIndex: 50,
          background: 'var(--bg-panel)', boxShadow: '-4px 0 16px rgba(0,0,0,0.15)',
          padding: '1rem', overflowY: 'auto',
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h3 style={{ margin: 0 }}>
              {editId ? 'Документ штатного розкладу' : 'Новий документ'}
              {posted && <span style={{ color: '#16a34a', marginLeft: 8 }}>✓ проведено</span>}
            </h3>
            <button onClick={close}>✕</button>
          </div>

          {err && <div style={{ color: '#dc2626', margin: '0.5rem 0' }}>{err}</div>}

          <div style={{ display: 'flex', gap: '0.6rem', flexWrap: 'wrap', marginTop: '0.6rem' }}>
            <label>Дата
              <DatePopover value={docDate} disabled={posted}
                     onChange={(v) => setDocDate(v)} />
            </label>
            <label>Вид
              <select value={kind} disabled={posted}
                      onChange={(e) => setKind(e.target.value as 'INITIAL' | 'CHANGE')}>
                <option value="INITIAL">Первинний</option>
                <option value="CHANGE">Зміна</option>
              </select>
            </label>
            <label style={{ flex: 1 }}>Коментар
              <input value={comment} disabled={posted}
                     onChange={(e) => setComment(e.target.value)} />
            </label>
          </div>

          <table className="data-table" style={{ width: '100%', marginTop: '0.8rem', fontSize: '0.85rem' }}>
            <thead><tr>
              <th>№</th><th>Підрозділ</th><th>Посада</th><th>Вид розрахунку</th><th>Ставки</th><th>Оклад</th><th></th>
            </tr></thead>
            <tbody>
              {items.map((it, idx) => (
                <tr key={idx}>
                  <td>{it.line_number}</td>
                  <td>
                    <select value={it.department_id} disabled={posted}
                            onChange={(e) => setRow(idx, { department_id: e.target.value })}>
                      <option value="">—</option>
                      {depts.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
                    </select>
                  </td>
                  <td>
                    <select value={it.position_id} disabled={posted}
                            onChange={(e) => setRow(idx, { position_id: e.target.value })}>
                      <option value="">—</option>
                      {positions.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                    </select>
                  </td>
                  <td>
                    <select value={it.calc_type_id} disabled={posted}
                            onChange={(e) => setRow(idx, { calc_type_id: e.target.value })}>
                      <option value="">—</option>
                      {calcTypes.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </select>
                  </td>
                  <td><input type="number" step="0.01" style={{ width: 70 }} value={it.rate_count}
                             disabled={posted} onChange={(e) => setRow(idx, { rate_count: e.target.value })} /></td>
                  <td><input type="number" step="0.01" style={{ width: 100 }} value={it.salary}
                             disabled={posted} onChange={(e) => setRow(idx, { salary: e.target.value })} /></td>
                  <td>{!posted && <button onClick={() => delRow(idx)}>✕</button>}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {!posted && (
            <div style={{ marginTop: 4, display: 'flex', gap: '0.5rem' }}>
              <button onClick={addRow}>+ Додати позицію</button>
              <button
                title="Заповнити позиції з діючих працівників (згорнуто по посаді+підрозділі) станом на дату документа"
                onClick={async () => {
                  const snap = await staffSchedules.snapshot(orgId, docDate, 'position');
                  // Підтягуємо всіх з посадою. Підрозділ необов'язковий (alembic 0305):
                  // штатне можна вести без нього (ФОП/один працівник/імпорт без підрозділу);
                  // за потреби бухгалтер обере підрозділ у рядку.
                  const valid = snap.filter((s) => s.position_id);
                  if (valid.length === 0) return;
                  // Вид розрахунку зі стану працівника (code) → id довідника calc_types.
                  const calcIdByCode = new Map(calcTypes.map((c) => [c.code, c.id]));
                  setItems(valid.map((s, i) => ({
                    line_number: i + 1,
                    department_id: (s.department_id as string) ?? '',
                    position_id: s.position_id as string,
                    rate_count: s.rate_count, salary: s.salary,
                    calc_type_id: (s.calc_type_code && calcIdByCode.get(s.calc_type_code)) || '',
                  })));
                }}
              >⤓ Заповнити з працівників</button>
            </div>
          )}

          <div style={{ marginTop: '1rem', display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
            {!posted && (
              <button className="primary" disabled={!canManage || !validItems || save.isPending}
                      onClick={() => save.mutate()}>Зберегти</button>
            )}
            {editId && !posted && (
              <button disabled={!canManage || doPost.isPending} onClick={() => doPost.mutate()}>▶ Провести</button>
            )}
            {editId && posted && (
              <button disabled={!canManage || doUnpost.isPending} onClick={() => doUnpost.mutate()}>◀ Розпровести</button>
            )}
            {editId && (
              <button onClick={() => window.open(`/staff-schedule/${editId}/print`, '_blank')}
                title="Друкована форма цього штатного розпису">
                друк
              </button>
            )}
            {editId && !posted && (
              <button disabled={!canManage} onClick={() => doDelete.mutate()}>видалити</button>
            )}
            <button onClick={close}>закрити</button>
          </div>
          {/* Вкладення (рішення власника 2026-08-13: на всіх документах). */}
          {editId && (
            <AttachmentsPanel organizationId={orgId}
              documentType="doc.staff_schedules" documentId={editId} />
          )}
        </div>
      )}
    </>
  );
}

// ============================================================ Зріз на дату

function SnapshotTab({ orgId, deptName, posName }: {
  orgId: string; deptName: Map<string, string>; posName: Map<string, string>;
}) {
  const [onDate, setOnDate] = useState(new Date().toISOString().slice(0, 10));
  const [groupBy, setGroupBy] = useState<'employee' | 'position'>('employee');
  const snap = useQuery({
    queryKey: ['staff-schedule-snapshot', orgId, onDate, groupBy],
    queryFn: () => staffSchedules.snapshot(orgId, onDate, groupBy),
    enabled: !!orgId && !!onDate,
  });
  const byEmp = groupBy === 'employee';

  const rows = useMemo(() => (snap.data ?? []).map((r, i) => ({
    rowId: `${r.department_id}:${r.position_id}:${r.employee_id ?? i}`,
    pib: r.employee_name ?? '',
    dept: r.department_id ? (deptName.get(r.department_id) ?? '—') : '—',
    pos: r.position_id ? (posName.get(r.position_id) ?? '—') : '—',
    calc: CALC_TYPE_LABELS[r.calc_type_code ?? ''] ?? (r.calc_type_code || '—'),
    rate: r.rate_count, salary: r.salary, monthlyFot: r.monthly_fot ?? r.salary,
  })).sort((a, b) => (byEmp ? a.pib.localeCompare(b.pib) : a.dept.localeCompare(b.dept) || a.pos.localeCompare(b.pos))),
  [snap.data, deptName, posName, byEmp]);

  const totalRate = rows.reduce((s, r) => s + Number(r.rate), 0);
  // Місячний ФОТ: сума monthly_fot (для погодинника = ставка×норма_годин, не ставка×1).
  // backend уже множить на к-сть людей у group_by=position.
  const totalSalary = rows.reduce((s, r) => s + Number(r.monthlyFot), 0);

  return (
    <div style={{ marginTop: '0.5rem' }}>
      <div className="toolbar" style={{ gap: '0.5rem', flexWrap: 'wrap' }}>
        <label>Станом на дату
          <DatePopover value={onDate} onChange={(v) => setOnDate(v)}
                 style={{ marginLeft: 6 }} />
        </label>
        <label>Подання
          <select value={groupBy} onChange={(e) => setGroupBy(e.target.value as 'employee' | 'position')}
                  style={{ marginLeft: 6 }}>
            <option value="employee">По працівниках</option>
            <option value="position">По посадах (згорнуто)</option>
          </select>
        </label>
        <button
          disabled={!orgId || rows.length === 0}
          onClick={() => window.open(
            `/staff-schedule/print?organization_id=${orgId}&on_date=${onDate}&group_by=${groupBy}`, '_blank')}
        >🖨 Друкувати</button>
      </div>
      <p style={{ color: 'var(--text-secondary)', fontSize: '0.82rem', margin: '0.3rem 0' }}>
        Штатний розклад станом на дату — будується з <b>діючих працівників</b> (їхній
        стан: посада, підрозділ, оклад). «По працівниках» — рядок на людину; «По посадах»
        — згорнуто (к-сть людей як ставки).
      </p>
      <table className="data-table" style={{ width: '100%', fontSize: '0.88rem' }}>
        <thead><tr>
          {byEmp && <th style={{ textAlign: 'left' }}>Працівник</th>}
          <th style={{ textAlign: 'left' }}>Підрозділ</th>
          <th style={{ textAlign: 'left' }}>Посада</th>
          <th style={{ textAlign: 'left' }}>Вид розрахунку</th>
          <th style={{ textAlign: 'right' }}>{byEmp ? 'Ставка' : 'К-сть (ставки)'}</th>
          <th style={{ textAlign: 'right' }}>Оклад / ставка</th>
          <th style={{ textAlign: 'right' }}>Місячний ФОТ</th>
        </tr></thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.rowId}>
              {byEmp && <td>{r.pib}</td>}
              <td>{r.dept}</td><td>{r.pos}</td><td>{r.calc}</td>
              <td style={{ textAlign: 'right' }}>{r.rate}</td>
              <td style={{ textAlign: 'right' }}>{Number(r.salary).toLocaleString('uk-UA')}</td>
              <td style={{ textAlign: 'right' }}>{Number(r.monthlyFot).toLocaleString('uk-UA')}</td>
            </tr>
          ))}
          {rows.length === 0 && (
            <tr><td colSpan={byEmp ? 7 : 6} style={{ color: 'var(--text-secondary)' }}>Немає діючих працівників на цю дату.</td></tr>
          )}
        </tbody>
        {rows.length > 0 && (
          <tfoot><tr style={{ fontWeight: 600 }}>
            <td colSpan={byEmp ? 4 : 3}>Разом</td>
            <td style={{ textAlign: 'right' }}>{totalRate.toFixed(2)}</td>
            <td style={{ textAlign: 'right' }}>—</td>
            <td style={{ textAlign: 'right' }}>{totalSalary.toLocaleString('uk-UA')}</td>
          </tr></tfoot>
        )}
      </table>
    </div>
  );
}

function extractErr(e: unknown): string {
  const r = e as { response?: { data?: { detail?: unknown } } };
  const d = r?.response?.data?.detail;
  if (typeof d === 'string') return d;
  if (Array.isArray(d)) return d.map((x) => (x as { msg?: string }).msg ?? '').join('; ');
  return 'Помилка збереження';
}
